/**
 * i18next setup — §12.1.
 *
 * Bundles are namespaced per portal and loaded on demand. The loader map comes
 * from `import.meta.glob`, so Vite emits one chunk per locale file and the
 * network only fetches the namespace a route actually asks for.
 */
import i18n, { type BackendModule, type ReadCallback } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { DEFAULT_LANGUAGE, DEFAULT_NAMESPACE, SUPPORTED_LANGUAGES } from './config'

type LocaleModule = { default: Record<string, unknown> }

/** `../../locales/en/common.json` -> loader */
const loaders = import.meta.glob<LocaleModule>('../../locales/*/*.json')

const lazyBundleBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read: (language: string, namespace: string, callback: ReadCallback) => {
    const loader = loaders[`../../locales/${language}/${namespace}.json`]

    if (!loader) {
      // A missing bundle is not fatal: i18next falls back to `en`, and to the
      // key itself if that is missing too. Failing hard here would blank a
      // whole portal over one untranslated namespace.
      callback(null, {})
      return
    }

    loader().then(
      (module) => callback(null, module.default),
      (error: unknown) => callback(error as Error, null),
    )
  },
}

export async function initI18n(): Promise<typeof i18n> {
  if (i18n.isInitialized) return i18n

  await i18n
    .use(lazyBundleBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      supportedLngs: [...SUPPORTED_LANGUAGES],
      fallbackLng: DEFAULT_LANGUAGE,
      defaultNS: DEFAULT_NAMESPACE,
      ns: [DEFAULT_NAMESPACE],
      load: 'languageOnly', // `ar-AE` and `ar-SA` both resolve to `ar`
      interpolation: {
        escapeValue: false, // React escapes already
      },
      detection: {
        order: ['querystring', 'localStorage', 'navigator'],
        lookupQuerystring: 'lang',
        lookupLocalStorage: 'nextscene.language',
        caches: ['localStorage'],
      },
      react: {
        useSuspense: true,
      },
    })

  return i18n
}

export { i18n }
